import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import remarkSmartypants from 'remark-smartypants';

/**
 * Replaces the server-side `markdown-it` render, which ran with
 * `{ html: false, linkify: true, breaks: true, typographer: true }` plus the
 * task-lists plugin. Plugin mapping:
 *
 * - `remark-gfm`        → task lists + autolinking (`linkify`)
 * - `remark-breaks`     → `breaks: true`
 * - `remark-smartypants`→ `typographer: true`
 *
 * `dashes: 'oldschool'` is not cosmetic: markdown-it renders `--` as an en dash
 * and `---` as an em dash, while smartypants defaults to `--` as an em dash.
 * Without this, every existing text with a double hyphen renders differently
 * than it does today. Caught by diffing real content across both stacks.
 *
 * **No `rehype-raw`.** Its absence is what preserves `html: false`: raw HTML in
 * markdown is rendered as text, not markup. Adding it would open the door to
 * HTML injection the day it renders anything a user can write, so don't —
 * likewise `dangerouslySetInnerHTML`.
 */
export function Markdown({
  children,
  prose,
  id,
  className,
}: {
  children: string;
  /** Typeset as prose (`.prose`); without it the output stays bare. */
  prose?: boolean;
  id?: string;
  className?: string;
}) {
  const rendered = (
    <ReactMarkdown
      remarkPlugins={[
        remarkGfm,
        remarkBreaks,
        [remarkSmartypants, { dashes: 'oldschool' }],
      ]}
    >
      {children}
    </ReactMarkdown>
  );
  if (!prose) return rendered;
  return (
    <div id={id} className={className ? `prose ${className}` : 'prose'}>
      {rendered}
    </div>
  );
}
